import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, Stack } from "grommet";
import { flatten } from "lodash";
import * as React from "react";
import { usePermission } from "../account/AccountStore";
import StyledModal from "../components/StyledModel";
import { MatchRow } from "./MatchRow";
import { MatchViewModel, removeOutcome, RemoveMatchOutcomeRequest } from "./MatchesStore";

const revertIcon = <FontAwesomeIcon icon={["fad", "rotate-left"]}></FontAwesomeIcon>;

interface MatchRowWithActionsProps {
    match: MatchViewModel;
    onRevert?: () => void;
}

export const MatchRowWithActions = ({ match, onRevert }: MatchRowWithActionsProps) => {
    const [showRevertModal, setShowRevertModal] = React.useState(false);
    const [isReverting, setIsReverting] = React.useState(false);
    const hasWritePermission = usePermission("sport:matches.write");

    const hasOutcomes = () => {
        // Check if match has any outcomes (either top-level or in participants)
        if (match.outcomes && match.outcomes.length > 0) return true;
        if (match.participants && match.participants.length > 0) {
            return match.participants.some(p => p.outcomes && p.outcomes.length > 0);
        }
        return false;
    };

    const handleRevert = async () => {
        setIsReverting(true);
        try {
            // Remove all outcomes from both top-level and participants
            const participantOutcomes = flatten(match.participants.map((p) => p.outcomes || []));
            const allOutcomes = [...(match.outcomes || []), ...participantOutcomes];
            
            await Promise.all(
                allOutcomes.map((outcome) => 
                    removeOutcome({ matchID: match.id, outcomeID: outcome.id } as RemoveMatchOutcomeRequest)
                )
            );
            setShowRevertModal(false);
            if (onRevert) {
                onRevert();
            }
        } catch (error) {
            console.error("Error reverting match results:", error);
        } finally {
            setIsReverting(false);
        }
    };

    return (
        <>
            <Stack anchor="top-right">
                <MatchRow match={match} />
                {hasWritePermission && hasOutcomes() && (
                    <Box 
                        pad={{ top: "small", right: "small" }}
                        style={{ pointerEvents: "none" }}
                    >
                        <Button
                            icon={revertIcon}
                            label="Revert"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShowRevertModal(true);
                            }}
                            size="small"
                            color="status-critical"
                            style={{
                                pointerEvents: "auto",
                                background: "rgba(255, 0, 0, 0.9)",
                                color: "white",
                                padding: "4px 12px",
                                borderRadius: "4px",
                                fontSize: "12px",
                                fontWeight: "bold",
                                border: "none",
                                cursor: "pointer"
                            }}
                        />
                    </Box>
                )}
            </Stack>

            <StyledModal isOpen={showRevertModal} setIsOpen={setShowRevertModal}>
                <Box gap="medium" pad="medium" width="medium">
                    <Box>
                        <h3 style={{ margin: 0, marginBottom: "8px" }}>Revert Match Results?</h3>
                        <p style={{ margin: 0, color: "var(--text-muted)" }}>
                            This will remove all outcomes for this match. This action cannot be undone.
                        </p>
                    </Box>
                    <Box direction="row" gap="small" justify="end">
                        <Button
                            label="Cancel"
                            onClick={() => setShowRevertModal(false)}
                            disabled={isReverting}
                        />
                        <Button
                            label={isReverting ? "Reverting..." : "Revert Results"}
                            onClick={handleRevert}
                            color="status-critical"
                            disabled={isReverting}
                            primary
                        />
                    </Box>
                </Box>
            </StyledModal>
        </>
    );
};